import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Switch,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useCreateMockContact } from "@/hooks/mock-api-calls/useCreateMockContact";
import { useCreateMockMatterContactConnection } from "@/hooks/mock-api-calls/useCreateMockMatterContactConnection";
import { useCreateMockMatterExpense } from "@/hooks/mock-api-calls/useCreateMockMatterExpense";
import { useCreateMockMatterFile } from "@/hooks/mock-api-calls/useCreateMockMatterFile";
import { useCreateMockMatterInsurance } from "@/hooks/mock-api-calls/useCreateMockMatterInsurance";
import { useCreateMockMatterLien } from "@/hooks/mock-api-calls/useCreateMockMatterLien";
import { useCreateMockMatterLitigationDate } from "@/hooks/mock-api-calls/useCreateMockMatterLitigationDate";
import { useCreateMockMatterNote } from "@/hooks/mock-api-calls/useCreateMockMatterNote";
import { useCreateMockMatterPropertyDamage } from "@/hooks/mock-api-calls/useCreateMockMatterPropertyDamage";
import { useCreateMockMatterSettlementItems } from "@/hooks/mock-api-calls/useCreateMockMatterSettlementItems";
import { useCreateMockMatterTask } from "@/hooks/mock-api-calls/useCreateMockMatterTask";
import { useCreateMockMatterVehicle } from "@/hooks/mock-api-calls/useCreateMockMatterVehicle";
import { useCreateMockMedicalTreatmentSubitems } from "@/hooks/mock-api-calls/useCreateMockMedicalTreatmentSubitems";
import { generateMockMatter } from "@/lib/data/factories/matter-factory";
import { useCreateMatter } from "@/lib/data/react-query/matters/matter";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { createContactConnectionChildInput } from "@/lib/utils/connections/item-contact-connections";
import { MATTER_PARTY_CONNECTION_TYPES } from "@/lib/utils/connections/matter-party-connections";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ChangeEvent, useCallback, useEffect, useState } from "react";

const TEST_FIRM_IDS = [
  "54ba8d0f48bb4370ab704ec217164160",
  "65d270fed2a04aa995c04b1131473483",
  "31b32f115041483aa0918bfadee264e7",
  "62fb75b731624361a1d982187ceb698a",
  "ad33fb5feda840f49042ecb9834041e7",
  "8141c0a9e7a248cab4ea13cdd3bdf460",
  "3d70e332e0d848bfa424217f9da6be15",
  "51f87b237d0045b09f8d3d7c0149c78c",
  "04aff5ff360c409f81988c8e5a001fd1",
  "0016abc026d04cf0abe910b31cc2fdb6",
  "637fab7f653945f5a4f1b33a373f2fe6",
  "6fc30ca8236e41cfbb47c5b54a32b9c3",
  "b1e7aec0d1004cc096a165ec8bdc5209",
  "640011d735504234aaf0fead29e6d6cc",
  "d21d7084689840c7a0d3e9e4a34e44d9",
  "fe4b5c3643474c9085bd664089d36aae",
  "df19f9a8d98943e1988cb78c38ccf58b",
  "99c2bab6d7a645e7a3af3f80cd207716",
  "cc24f2ee4f09457f8e5694603a350100",
  "ed2b9087de5942c09e5187758926682c",
];
const DEFAULT_MATTER_INTERVAL_SECONDS = 15;
const MIN_MATTER_INTERVAL_SECONDS = 5;
const getRandomItemCount = (min = 0, max = 5) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export default function FirmTestButtonsCard() {
  const { currentUser } = useUser();
  const { data: matterSettingsData } = useQuery(
    retrieveMatterSettingsQueryOptions(),
  );
  const createMatter = useCreateMatter();
  const { createMockContact, isLoading: isCreatingContact } =
    useCreateMockContact({ includeSubItems: false });

  const {
    createMockMedicalTreatment,
    createMockMedicalTreatmentAppointment,
    createMockMedicalTreatmentRecord,
    createMockMedicalTreatmentBill,
  } = useCreateMockMedicalTreatmentSubitems();
  const { createMockMatterContactConnection } =
    useCreateMockMatterContactConnection();
  const { createMockMatterLien } = useCreateMockMatterLien();
  const { createMockMatterInsurance } = useCreateMockMatterInsurance();
  const { createMockMatterVehicle } = useCreateMockMatterVehicle();
  const { createMockMatterPropertyDamage } =
    useCreateMockMatterPropertyDamage();
  const { createMockMatterExpense } = useCreateMockMatterExpense();
  const { createMockMatterSettlementDemand, createMockMatterSettlementOffer } =
    useCreateMockMatterSettlementItems();
  const { createMockMatterTask } = useCreateMockMatterTask();
  const { createMockMatterNote } = useCreateMockMatterNote();
  const { createMockMatterFile } = useCreateMockMatterFile();
  const { createMockMatterLitigationDate } =
    useCreateMockMatterLitigationDate();

  const firmId = currentUser.firm.id;
  const shouldShowCard = !!firmId && TEST_FIRM_IDS.includes(firmId);
  const defaultMatterStatus = matterSettingsData?.data.statuses[0];
  const defaultIncidentType = matterSettingsData?.data.incidentTypes?.[0];
  const defaultMatterTeamId = matterSettingsData?.data.teams[0]?.id;
  const defaultLitigationDateTypeId =
    matterSettingsData?.data.litigationDateTypes[0]?.id;
  const [isAutoCreateEnabled, setIsAutoCreateEnabled] = useState(false);
  const [matterIntervalSeconds, setMatterIntervalSeconds] = useState(
    DEFAULT_MATTER_INTERVAL_SECONDS,
  );
  const isCreateDisabled =
    createMatter.isPending ||
    isCreatingContact ||
    !defaultMatterStatus ||
    !defaultIncidentType;
  const isMissingDefaultMatterConfig =
    !defaultMatterStatus || !defaultIncidentType;
  const normalizedIntervalSeconds = Math.max(
    matterIntervalSeconds,
    MIN_MATTER_INTERVAL_SECONDS,
  );

  const handleCreateMatter = useCallback(async () => {
    if (!defaultMatterStatus || !defaultIncidentType) {
      return;
    }

    const mockMatter = generateMockMatter();
    const medicalTreatmentCount = getRandomItemCount(1, 5);
    const medicalTreatmentAppointmentCount = getRandomItemCount();
    const medicalTreatmentRecordCount = getRandomItemCount();
    const medicalTreatmentBillCount = getRandomItemCount();
    const clientInsuranceCount = getRandomItemCount(1, 5);
    const defendantInsuranceCount = getRandomItemCount();
    const clientVehicleCount = getRandomItemCount();
    const defendantVehicleCount = getRandomItemCount();
    const propertyDamageCount = getRandomItemCount();
    const expenseCount = getRandomItemCount();
    const lienCount = getRandomItemCount();
    const taskCount = getRandomItemCount();
    const noteCount = getRandomItemCount();
    const fileCount = getRandomItemCount();
    const settlementDemandCount = getRandomItemCount();
    const settlementOfferCount = getRandomItemCount();
    const litigationDateCount = getRandomItemCount();

    try {
      // Create Contact
      const clientContactResponse = await createMockContact();
      const createdClientContact = clientContactResponse.data;

      const defendantContactResponse = await createMockContact();
      const createdDefendantContact = defendantContactResponse.data;

      // Create Matter
      const createdMatter = await createMatter.mutateAsync({
        attorneyFee: mockMatter.attorneyFee ?? 0,
        clientMaritalStatus: mockMatter.clientMaritalStatus,
        clientNoSocialSecurity: mockMatter.clientNoSocialSecurity,
        connections: [
          createContactConnectionChildInput(
            createdClientContact.id,
            MATTER_PARTY_CONNECTION_TYPES.Client,
          ),
          createContactConnectionChildInput(
            createdClientContact.id,
            MATTER_PARTY_CONNECTION_TYPES["Primary Contact"],
          ),
        ],
        dateOfLoss: mockMatter.dateOfLoss ?? "",
        incidentAddressCity: mockMatter.incidentAddressCity,
        incidentAddressCountry: mockMatter.incidentAddressCountry ?? "",
        incidentAddressState: mockMatter.incidentAddressState ?? "",
        incidentAddressStreet1: mockMatter.incidentAddressStreet1,
        incidentAddressStreet2: mockMatter.incidentAddressStreet2,
        incidentAddressZip:
          typeof mockMatter.incidentAddressZipCode === "string"
            ? mockMatter.incidentAddressZipCode
            : null,
        incidentDescription:
          typeof mockMatter.description === "string"
            ? mockMatter.description
            : null,
        statusId: defaultMatterStatus.id,
        teamId: defaultMatterTeamId,
        title: mockMatter.title,
        incidentTypeId: defaultIncidentType.id,
      });
      const createdMatterId = createdMatter.data.id;

      await createMockMatterContactConnection(
        createdDefendantContact,
        createdMatterId,
        "Defendant",
      );

      // Create Medical Treatment
      const medicalTreatments =
        medicalTreatmentCount > 0
          ? await createMockMedicalTreatment(
              createdMatterId,
              medicalTreatmentCount,
            )
          : [];
      const medicalTreatment = medicalTreatments.at(0);

      const clientInsurances =
        clientInsuranceCount > 0
          ? await createMockMatterInsurance(
              createdMatterId,
              createdClientContact,
              clientInsuranceCount,
            )
          : [];
      const clientInsurance = clientInsurances.at(0);
      const settlementInsuranceId = clientInsurance?.data.id;

      const creationTasks: Promise<unknown>[] = [
        // Create Lien
        createMockMatterLien(createdMatterId, createdClientContact, lienCount),
        // Create Insurance
        createMockMatterInsurance(
          createdMatterId,
          createdDefendantContact,
          defendantInsuranceCount,
        ),
        // Create Vehicle
        createMockMatterVehicle(
          createdMatterId,
          createdClientContact,
          clientVehicleCount,
        ),
        createMockMatterVehicle(
          createdMatterId,
          createdDefendantContact,
          defendantVehicleCount,
        ),
        // Create Property Damage
        createMockMatterPropertyDamage(createdMatterId, propertyDamageCount),

        // Create Expense
        createMockMatterExpense(createdMatterId, expenseCount),
        // Create Task
        createMockMatterTask(createdMatterId, taskCount),
        // Create Note
        createMockMatterNote(createdMatterId, noteCount),
        // Create File
        createMockMatterFile(createdMatterId, fileCount),
      ];

      if (medicalTreatment) {
        creationTasks.push(
          // Create Medical Treatment Appointment
          createMockMedicalTreatmentAppointment(
            medicalTreatment.data.id,
            createdMatterId,
            medicalTreatmentAppointmentCount,
          ),
          // Create Medical Treatment Record
          createMockMedicalTreatmentRecord(
            createdMatterId,
            medicalTreatment.data.id,
            medicalTreatmentRecordCount,
          ),
          // Create Medical Treatment Bill
          createMockMedicalTreatmentBill(
            createdMatterId,
            medicalTreatment.data.id,
            medicalTreatmentBillCount,
          ),
        );
      }

      if (settlementInsuranceId) {
        creationTasks.push(
          // Create Settlement Demand
          createMockMatterSettlementDemand(
            createdMatterId,
            settlementInsuranceId,
            settlementDemandCount,
          ),
          // Create Settlement Offer
          createMockMatterSettlementOffer(
            createdMatterId,
            settlementInsuranceId,
            settlementOfferCount,
          ),
        );
      }

      if (defaultLitigationDateTypeId) {
        creationTasks.push(
          createMockMatterLitigationDate(
            createdMatterId,
            defaultLitigationDateTypeId,
            litigationDateCount,
          ),
        );
      }

      await Promise.all(creationTasks);
    } catch (error) {
      console.error("Failed to create mock contact for matter:", error);
    }
  }, [
    createMatter,
    createMockMedicalTreatment,
    createMockMedicalTreatmentAppointment,
    createMockMedicalTreatmentRecord,
    createMockMedicalTreatmentBill,
    createMockMatterContactConnection,
    createMockMatterTask,
    createMockMatterNote,
    createMockMatterVehicle,
    createMockMatterPropertyDamage,
    createMockMatterExpense,
    createMockMatterSettlementDemand,
    createMockMatterSettlementOffer,
    createMockMatterLitigationDate,
    createMockMatterLien,
    createMockMatterInsurance,
    createMockMatterFile,
    createMockContact,
    defaultMatterStatus,
    defaultMatterTeamId,
    defaultIncidentType,
    defaultLitigationDateTypeId,
  ]);

  useEffect(() => {
    if (!isAutoCreateEnabled || isCreateDisabled) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void handleCreateMatter();
    }, normalizedIntervalSeconds * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [
    handleCreateMatter,
    isAutoCreateEnabled,
    isCreateDisabled,
    normalizedIntervalSeconds,
  ]);

  const handleIntervalChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = Number(event.target.value);

    if (Number.isNaN(nextValue)) {
      setMatterIntervalSeconds(MIN_MATTER_INTERVAL_SECONDS);
      return;
    }

    setMatterIntervalSeconds(Math.max(nextValue, MIN_MATTER_INTERVAL_SECONDS));
  };

  if (!shouldShowCard) {
    return null;
  }

  return (
    <Card>
      <CardContent className="w-full pt-4">
        <div className="mb-8 gap-y-1">
          <Text variant="title">Firm test buttons</Text>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="border-border flex flex-wrap items-center gap-3 rounded-md border px-3 py-2">
            <div className="flex items-center gap-2">
              <Switch
                id="auto-create-matter"
                checked={isAutoCreateEnabled}
                onCheckedChange={setIsAutoCreateEnabled}
                disabled={isCreateDisabled}
                aria-label="Auto-create matters"
              />
              <Label
                htmlFor="auto-create-matter"
                className="text-sm font-medium"
              >
                Auto-create matter
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={MIN_MATTER_INTERVAL_SECONDS}
                value={matterIntervalSeconds}
                onChange={handleIntervalChange}
                disabled={!isAutoCreateEnabled}
                className="h-8 w-20"
                aria-label="Auto-create matter interval in seconds"
              />
              <Text as="span" variant="subHeading">
                seconds
              </Text>
            </div>
          </div>
          {isMissingDefaultMatterConfig && (
            <div className="flex flex-wrap items-center gap-2">
              <Text as="p" variant="subHeading" color="destructive">
                Auto-create is disabled since there is no default matter status
                or type for this firm.
              </Text>
              <Button asChild size="sm" variant="outline">
                <Link to="/settings/matter-defaults">View matter defaults</Link>
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
